import { useState } from 'react'
import {X, Upload, Loader2, Search, FileText} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import {
  CASE_CATEGORIES_TO_COLLABORATOR,
  CASE_CATEGORIES_TO_COMPANY,
} from '@/lib/caseCategories'
import type { WorkCase } from '@/hooks/useCases'

interface CaseReportModalProps {
  reporterRole: 'empresa' | 'colaborador'
  reporterUserId: string
  reporterName?: string
  onClose: () => void
  onSubmit: (data: Partial<WorkCase>) => Promise<void>
}

interface TargetOption {
  userId: string
  name: string
  email?: string
}

function CaseReportModal({
  reporterRole,
  reporterUserId,
  reporterName,
  onClose,
  onSubmit,
}: CaseReportModalProps) {
  // Si reporta una empresa, el objetivo es un colaborador; si reporta un colaborador, el objetivo es una empresa
  const targetType: 'colaborador' | 'empresa' = reporterRole === 'empresa' ? 'colaborador' : 'empresa'
  const categories = reporterRole === 'empresa' ? CASE_CATEGORIES_TO_COLLABORATOR : CASE_CATEGORIES_TO_COMPANY

  const [search, setSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<TargetOption[]>([])
  const [target, setTarget] = useState<TargetOption | null>(null)
  const [category, setCategory] = useState(categories[0]?.value || '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [evidenceUrls, setEvidenceUrls] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSearchTarget = async () => {
    if (!search.trim()) return
    setSearching(true)
    setError('')
    try {
      if (targetType === 'colaborador') {
        const res = await lumi.entities.collaborators.list({ filter: { email: search.trim() }, limit: 5 })
        setResults(res.list.map((c: any) => ({ userId: c.userId, name: c.fullName || c.email, email: c.email })))
      } else {
        const res = await lumi.entities.companies.list({ limit: 50 })
        const term = search.trim().toLowerCase()
        setResults(
          res.list
            .filter((c: any) => (c.tradeName || c.legalName || '').toLowerCase().includes(term))
            .slice(0, 5)
            .map((c: any) => ({ userId: c.userId, name: c.tradeName || c.legalName, email: c.email }))
        )
      }
    } catch (e) {
      console.error('Error al buscar objetivo del reporte:', e)
      setError('No se pudo realizar la búsqueda.')
    } finally {
      setSearching(false)
    }
  }

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    setError('')
    try {
      const results = await lumi.tools.file.upload(Array.from(files))
      const urls = results.filter((r: any) => r.fileUrl).map((r: any) => r.fileUrl as string)
      setEvidenceUrls((prev) => [...prev, ...urls])
      const failed = results.filter((r: any) => r.uploadError)
      if (failed.length > 0) setError(`Algunos archivos no se pudieron subir (${failed.length}).`)
    } catch (e) {
      console.error('Error al subir evidencias:', e)
      setError('Ocurrió un error al subir las evidencias.')
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async () => {
    if (!target) {
      setError(`Selecciona ${targetType === 'colaborador' ? 'al colaborador' : 'a la empresa'} a reportar.`)
      return
    }
    if (!title.trim() || !description.trim()) {
      setError('Completa el título y la descripción del caso.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      await onSubmit({
        reporterUserId,
        reporterRole,
        reporterName,
        targetType,
        targetUserId: target.userId,
        targetName: target.name,
        category,
        title: title.trim(),
        description: description.trim(),
        evidenceUrls,
        status: 'abierto',
      })
      onClose()
    } catch (e) {
      console.error('Error al crear caso:', e)
      setError('No se pudo registrar el caso.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {reporterRole === 'empresa' ? 'Reportar conducta de un colaborador' : 'Reportar a una empresa'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Buscar objetivo */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">
              {targetType === 'colaborador' ? 'Colaborador a reportar (correo)' : 'Empresa a reportar (nombre)'}
            </label>
            {target ? (
              <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <span className="text-sm font-medium text-emerald-800">{target.name}</span>
                <button onClick={() => setTarget(null)} className="text-xs text-emerald-700 hover:underline">
                  Cambiar
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={targetType === 'colaborador' ? 'correo@ejemplo.com' : 'Nombre de la empresa'}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  />
                  <button
                    onClick={handleSearchTarget}
                    disabled={searching}
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-amber-400 hover:bg-slate-800 disabled:opacity-60"
                  >
                    {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Buscar
                  </button>
                </div>
                {results.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {results.map((r) => (
                      <button
                        key={r.userId}
                        onClick={() => {
                          setTarget(r)
                          setResults([])
                        }}
                        className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:border-amber-300 hover:bg-amber-50"
                      >
                        <span className="font-medium text-slate-800">{r.name}</span>
                        {r.email && <span className="text-xs text-slate-400">{r.email}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Categoría */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">Categoría del caso</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Título */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">Título</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Resumen breve del caso"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">Descripción de los hechos</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Describe con detalle qué ocurrió, cuándo y cómo."
              className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>

          {/* Evidencias */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">Evidencias (opcional)</label>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500 hover:border-amber-300 hover:bg-amber-50">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? 'Subiendo...' : 'Adjuntar archivos'}
              <input type="file" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
            </label>
            {evidenceUrls.length > 0 && (
              <ul className="mt-2 space-y-1">
                {evidenceUrls.map((url, i) => (
                  <li key={url} className="flex items-center gap-2 text-xs text-slate-600">
                    <FileText size={13} className="text-amber-500" />
                    <span className="truncate">Evidencia {i + 1}</span>
                    <button
                      onClick={() => setEvidenceUrls((prev) => prev.filter((u) => u !== url))}
                      className="text-red-500 hover:underline"
                    >
                      quitar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300 disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Enviando...' : 'Abrir caso'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CaseReportModal
